import React, {useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "tools/routerCompat";
import {
  DATA_TYPE_DATE,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_MONEY, DATA_TYPE_NUMBER, DATA_TYPE_NUMBER_TENTHS,
  DATA_TYPE_PERCENT_DECIMAL,
} from "typeConstants/dataTypes";
import PropTypes from "prop-types";
import MCard from "components/Shared/MCard.tsx";
import { MGrid, MSpinner, MTable} from "components/Shared";
import { getInspectorHiringForecast, getInspectorHiringForecastHistory, updateHiringStatus, getInspectorHiringNotes, postInspectorHiringNote } from "requests/hiringNeed";
import { inspectorHiringForecastStatus } from "typeConstants/quicklistFilters";
import Grid from "@mui/material/Grid";
import { dateTimeToStringWithDOW, dateToString } from "components/Shared/Formatters";
import NoteInputField from "components/Shared/NoteInputField";
import * as employeeRoles from "typeConstants/employeeRoles";
import { getEmployeesByRoles } from "requests/employee";

const HiringNeed = (props) => {
  const mounted = useRef(true);

  const [hiringForecast, setHiringForecast] = useState([]);
  const [isFetchingForecast, setIsFetchingForecast] = useState(true);
  const [hiringNotes, setHiringNotes] = useState([]);
  const [isFetchingNotes, setIsFetchingNotes] = useState(true);
  const [isUpdatingHiringStatus, setIsUpdatingHiringStatus] = useState(false);
  const [fieldDevelopmentManagers, setFieldDevelopmentManagers] = useState(null);
  const [hiringForecastHistory, setHiringForecastHistory] = useState([]);
  const [isFetchingForecastHistory, setIsFetchingForecastHistory] = useState(true);

  const fetchData = () => {
    getInspectorHiringForecast(props.location.state ? props.location.state.id : props.inspectorHiringForecastId, (res) => {
      res.createdDateTimeUtc = dateToString(new Date(res.createdDateTimeUtc));
      setHiringForecast(res);
      setIsFetchingForecast(false);
    }, mounted);

    getInspectorHiringForecastHistory(props.location.state ? props.location.state.id : props.inspectorHiringForecastId, (res) => {
      res.sort((a, b) => new Date(b.createdDateTimeUtc) - new Date(a.createdDateTimeUtc));
      res.map(r => r.createdDateTimeUtc = dateToString(new Date(r.createdDateTimeUtc)));
      setHiringForecastHistory(res);
      setIsFetchingForecastHistory(false);
    }, mounted);

    getInspectorHiringNotes(props.location.state ? props.location.state.id : props.inspectorHiringForecastId, (res) => {
      setHiringNotes(res);
      setIsFetchingNotes(false);
    }, mounted);

    if (!fieldDevelopmentManagers) getEmployeesByRoles([employeeRoles.FIELD_DEVELOPMENT_MANAGER], data => setFieldDevelopmentManagers(data), mounted);
  };

  useEffect(() => fetchData(), [props.inspectorHiringForecastId]);

  const hiringForecastFields = [{
    label: "Hiring ID",
    value: hiringForecast.id,
    editable: false
    },
    {
      label: "Service Region",
      value: hiringForecast.serviceRegion?.name,
      editable: false
    },
    {
      label: "State",
      value: hiringForecast.serviceRegion?.state?.name,
      editable: false
    },
    {
      label: "Counties",
      value: hiringForecast.serviceRegion?.countyList?.map(x => x.name)?.join(","),
      editable: false
    },
    {
      label: "Logistics Manager",
      value: hiringForecast.logisticsManager,
      editable: false
    },
    {
      label: "Created Date",
      dataType: DATA_TYPE_DATE,
      value: hiringForecast.createdDateTimeUtc,
      editable: false
    },
    {
      label: "Updated Date",
      dataType: DATA_TYPE_DATE,
      value: hiringForecast.updatedDateTimeUtc,
      editable: false
    },
    {
      label: "Deficit",
      value: hiringForecast.deficit,
      editable: false,
      tooltipSimple: "Estimated number of needed producer FRs to match meet ordering trends and standard SLA for given area",
      dataType: DATA_TYPE_NUMBER,
      dataTypeOptions: {numDigitsAfterDecimal: 1}
    },
    {
      label: "Travel Adj. Deficit",
      value: hiringForecast.deficitLocal,
      editable: false,
      tooltipSimple: "Estimated number of needed producer FRs for an area when factoring in temporary travel support",
      dataType: DATA_TYPE_NUMBER,
      dataTypeOptions: {numDigitsAfterDecimal: 1}
    },
    {
      label: "# Trainees",
      value: hiringForecast.traineeCount,
      editable: false,
      tooltipSimple: "The number of active FRs in Trainee status",
    },
    {
      label: "Trainee Adjusted Deficit",
      value: hiringForecast.deficitLocalTrainee,
      editable: false,
      tooltipSimple: "The adjusted deficit when factoring in new trainees entering production",
      dataType: DATA_TYPE_NUMBER,
      dataTypeOptions: {numDigitsAfterDecimal: 1}
    },
    {
      label: "Ordering Trend",
      value: hiringForecast.orderingTrend,
      editable: false,
      tooltipSimple: "The recent ordering trend in a given area, relative to historical average",
      dataType: DATA_TYPE_PERCENT_DECIMAL
    },
    {
      label: "FR Utilization",
      value: hiringForecast.inspectorUtilization,
      editable: false,
      tooltipSimple: "The percentage of all FRs in a given area that consistently operate at a 'producer' level",
      dataType: DATA_TYPE_PERCENT_DECIMAL,
    },
    {
      label: "Advertising ROP",
      value: hiringForecast.rateOfPay,
      editable: false,
      tooltipSimple: "Average FR wage paid by MSA",
      dataType: DATA_TYPE_MONEY,
      dataTypeOptions: {numDigitsAfterDecimal: 2}
    },
    {
      key: "status",
      label: "Status",
      dataType: DATA_TYPE_DROPDOWN,
      dataTypeOptions: { options: inspectorHiringForecastStatus, nullable: false },
      value: inspectorHiringForecastStatus.find(x => x.value === hiringForecast.status?.toString()),
      editable: true
    },
    {
      key: "fieldDevelopmentManager",
      label: "Assigned To",
      value: hiringForecast.assignedToEmployee ? {label: hiringForecast.assignedToEmployee.firstLastName, value: hiringForecast.assignedToEmployee.firstLastName} : "",
      editable: true,
      dataType: DATA_TYPE_DROPDOWN,
      dataTypeOptions: { options: fieldDevelopmentManagers?.map(x => ({ label: `${x.firstName} ${x.lastName}`, value: x.employeeId })), nullable: false }
    }
  ];

  const formatNoteDateCreated = note => dateTimeToStringWithDOW(new Date(note.createdDateTimeUtc + "Z"));

  const notesColDefs = [
    { name: "Date", dataSource: formatNoteDateCreated, style: { width: "200px" } },
    { name: "Created By", dataSource: "createdByInternetUserNameInfo.fullName", style: { width: "200px" } },
    { name: "Note", dataSource: "note", className: "hiring-notes-note" },
  ];

  const hiringHistoryColDefs = [
    { key: "createdDateTimeUtc", name: "Date", dataSource: "createdDateTimeUtc", sortable: false },
    { key: "deficit", name: "Deficit", dataSource: "deficit", sortable: false, dataType: DATA_TYPE_NUMBER_TENTHS, headerTooltip: "Estimated number of needed producer FRs to match meet ordering trends and standard SLA for given area" },
    { key: "deficitLocal", name: "Travel Adj. Deficit", dataSource: "deficitLocal", sortable: false, dataType: DATA_TYPE_NUMBER_TENTHS, headerTooltip: "Estimated number of needed producer FRs for an area when factoring in temporary travel support" },
    { key: "traineeCount", name: "# Trainees", dataSource: "traineeCount", sortable: false, headerTooltip: "The number of active FRs in Trainee status" },
    { key: "deficitLocalTrainee", name: "Trainee Adjusted Deficit", dataSource: "deficitLocalTrainee", sortable: false, dataType: DATA_TYPE_NUMBER_TENTHS, headerTooltip: "The adjusted deficit when factoring in new trainees entering production" },
    { key: "orderingTrend", name: "Ordering Trend", dataSource: "orderingTrend", sortable: false, headerTooltip: "The recent ordering trend in a given area, relative to historical average", dataType: DATA_TYPE_PERCENT_DECIMAL },
    { key: "inspectorUtilization", name: "FR Utilization", dataSource: "inspectorUtilization", sortable: false, headerTooltip: "The percentage of all FRs in a given area that consistently operate at a 'producer' level", dataType: DATA_TYPE_PERCENT_DECIMAL },
    { key: "rateOfPay", name: "Advertising ROP", dataSource: "rateOfPay", sortable: false, headerTooltip: "Average FR wage paid by MSA", dataType: DATA_TYPE_MONEY },
  ];

  const saveNote = noteText => {
    const req = { inspectorHiringForecastId: hiringForecast.id, createdByInternetUserId: props.internetUserId, note: noteText };
    postInspectorHiringNote(hiringForecast.id, req, () => fetchData(), mounted);
  };

  const hiringNotesFields = [
    {
      control:
        <NoteInputField
          saveNote={saveNote}
          placeholder="Add Hiring Note"
        />
    },
    {
      control:
        <MTable
          columnDefinitions={notesColDefs}
          data={hiringNotes}
        />
    }
  ];

  const hiringHistoryFields = [
    {
      control:
        <MGrid wrapperStyle={{ height: 400 }}
          columnDefinitions={hiringHistoryColDefs}
          data={hiringForecastHistory}
          disableFilters
          isFetchingData={isFetchingForecastHistory}
        />
    }
  ];

  const saveHiringForecastChanges = (arr, obj) => {
    setIsUpdatingHiringStatus(true);
    updateHiringStatus(hiringForecast.id, {id: hiringForecast.id, status: obj.status || hiringForecast.status, assignedToEmployeeId: obj.fieldDevelopmentManager || hiringForecast.assignedToEmployee?.employeeId}, () => {
      fetchData();
      setIsUpdatingHiringStatus(false);
    }, mounted);
  };

  if (isFetchingForecast || isFetchingNotes || isUpdatingHiringStatus || isFetchingForecastHistory) return <MSpinner />;
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <MCard title="Hiring Forecast" fields={hiringForecastFields} isEditable onEditFinished={saveHiringForecastChanges}/>
      </Grid>
      <Grid item xs={12}>
        <MCard title="Hiring Deficit History" fields={hiringHistoryFields} />
      </Grid>
      <Grid item xs={12}>
        <MCard className="hiring-notes" title="Hiring Notes" fields={hiringNotesFields} disableHoverStyles/>
      </Grid>
    </Grid>
  );
};


HiringNeed.propTypes = {
  history: PropTypes.object.isRequired,
  internetUserId: PropTypes.number.isRequired,
  inspectorHiringForecastId: PropTypes.number,
};

const mapStateToProps = state => ({
  internetUserId: state.user.profile.internetUserId,
});

export default connect(mapStateToProps, null)(withRouter(HiringNeed));
